/**
 * Générateur — Automatisme : addition et soustraction de fractions simples :
 * 3/5 + 4/5 ; 1 − 2/3 ; 4/7 − 2/21 ; 3 + 2/5 ; 2/5 + 1/4.
 * Attendu ID : 1054 | Niveau : 5ème | Domaine : Nombres rationnels
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 8). Cadrage rédigé
 * par le sous-agent programme-maths, passé à l'épreuve de Musk et validé
 * par le prof le 2026-09-09.
 *
 * Le geste : devant deux termes, voir d'un regard le dénominateur commun
 * (le même, le plus grand, ou le produit de deux petits), convertir avec un
 * fait de table, ajouter ou retrancher les numérateurs, écrire le résultat
 * en UNE fraction. Jamais de dénominateur commun à chercher (objectif 924,
 * puis 4e), jamais de simplification (le résultat est irréductible par
 * construction), jamais trois termes, jamais de problème (925), jamais
 * d'égalité à trou (1046), jamais entier + fraction en résultat (1053).
 *
 * Quatre cas, un geste chacun (entier_un du cadrage fondu dans entier à
 * l'épreuve de Musk : 1 − 2/3 n'isole rien que 2 − 2/3 ne teste, et c'est
 * la ligne où « le bas moins le haut » tombe juste par accident) :
 *   meme_den  : 3/5 + 4/5 = 7/5, rien à convertir ;
 *   entier    : 3 + 2/5 = 17/5, 2 − 2/3 = 4/3, l'entier s'écrit sur le
 *               dénominateur (n de 1 à 5 ; n ≥ 2 en Série flash, le
 *               DISCRIMINANT : avec 1 − 2/3, « bas moins haut » donne 1/3) ;
 *   multiples : 4/7 − 2/21 = 10/21, un seul terme à convertir, grand
 *               dénominateur à gauche ou à droite ;
 *   premiers  : 2/5 + 1/4 = 13/20, deux petits dénominateurs premiers entre
 *               eux, le commun est le produit (le seul cas neuf en 5e).
 *
 * Plan de cartes validé, deux cartes :
 *   - Série flash                     (4 lignes, une par cas, saisie fraction
 *                                      deux cases)
 *   - Le même dénominateur d'abord    (QCM 4 choix, Vrai/Faux 3 égalités ; le
 *                                      QCM, supprimé à la revue pour une
 *                                      fuite de position, a été remis le même
 *                                      jour : seul format dépouillable en
 *                                      mode classe QR code, cf. carte 2)
 * Supprimés à l'épreuve de Musk : Relier (redite de la Série flash), familles
 * addition (1051), oubli_entier, sens_inverse ; règle du « ; » au print (zéro
 * cas).
 *
 * ── Domaine ──────────────────────────────────────────────────────────────
 * Fractions de l'énoncé plus petites que 1, irréductibles. Résultat
 * strictement positif, numérateur ≤ 50, PREMIER avec le dénominateur commun
 * (une seule écriture juste), donc jamais entier. Un élève qui écrit 70/147
 * pour 4/7 − 2/21 (produit des dénominateurs) est compté faux : l'automatisme
 * est justement de voir que 21 suffit ; décision de scoring notée, comme sur
 * 1049, à rouvrir si le cas se voit en classe.
 * SACRIFIÉS : les quatre exemples de la fiche, une page par cas (décision
 * prof 2026-09-09, la fiche montre d'abord QUEL cas on reconnaît) : 3/5 + 4/5,
 * 3 + 2/5 (le 17/5 de la fiche 1053, même nombre dans les deux sens), 2/3 +
 * 1/6, 2/5 + 1/4, sous toutes leurs formes ; par construction, deux opérandes
 * pris dans le même ensemble ne sont jamais tirés ensemble.
 *
 * ── Distracteurs (familles nommées, cohérentes entre les cartes) ─────────
 *   bas_additionnes     : (x ± y)/(b + d), la fraction vue comme deux
 *                         entiers séparés — L'ERREUR PRINCIPALE ; pour un
 *                         entier, (n ± a)/(1 + b) ;
 *   entier_pas_converti : (n ± a)/b, l'entier posé sur le dénominateur sans
 *                         être multiplié ;
 *   pas_converti        : le bon dénominateur commun, un ou deux numérateurs
 *                         pris tels quels (4/7 − 2/21 = 2/21 ; 2/5 + 1/4 =
 *                         3/20 ou 9/20) ;
 *   coller              : les chiffres collés, 3 + 2/5 = 32/5 (addition
 *                         entier + fraction seulement) ;
 *   voisin_meme_table   : la conversion avec un fait de table voisin
 *                         (4/7 = 8/21 ou 16/21 ; 3 = 14/5 ou 16/5).
 * Chaque distracteur est vérifié strictement positif, différent de la clé
 * et des autres EN VALEUR ; collision → nouveau tirage.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 1054;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_MEME = 'Le même dénominateur d\'abord';

// ─── Le domaine ───────────────────────────────────────────────────────────

const CAS = ['meme_den', 'entier', 'multiples', 'premiers'];
const NUM_MAX = 50;
const KB_MAX = 30;
const COUPLES_PREMIERS = [[2, 3], [2, 5], [3, 4], [2, 7], [3, 5], [2, 9], [4, 5]];

/**
 * Les quatre exemples de la fiche (une page par cas), sacrifiés : deux
 * opérandes pris dans le même ensemble ne sont jamais tirés ensemble, sous
 * aucune forme (3/5 + 4/5 et 4/5 + 3/5 ; 3 + 2/5 ; 2/3 + 1/6, 1/6 + 2/3,
 * 5/6 − 1/6, 5/6 − 2/3 ; 2/5 + 1/4 et 1/4 + 2/5). Un entier est { a: n, b: 1 }.
 */
export const SACRIFIES = [
    [{ a: 3, b: 5 }, { a: 4, b: 5 }, { a: 7, b: 5 }],
    [{ a: 3, b: 1 }, { a: 2, b: 5 }, { a: 17, b: 5 }],
    [{ a: 2, b: 3 }, { a: 1, b: 6 }, { a: 5, b: 6 }],
    [{ a: 2, b: 5 }, { a: 1, b: 4 }, { a: 13, b: 20 }],
];

export const gcd = (x, y) => (y === 0 ? x : gcd(y, x % y));
const memeFraction = (f, g) => f.a === g.a && f.b === g.b;
const estSacrifie = (g, d) => SACRIFIES.some((s) => s.some((f) => memeFraction(f, g)) && s.some((f) => memeFraction(f, d)));

/** Un opérande : fraction { a, b } (b ≥ 2) ou entier { a: n, b: 1 }. */
export const frac = (a, b) => ({ a, b });
const entier = (n) => ({ a: n, b: 1 });
const estEntier = (f) => f.b === 1;
/** Valeur d'un calcul « g op d » sur le dénominateur commun `den` : { num, den }. */
const resultat = (g, op, d, den) => ({ num: (g.a * den) / g.b + (op === '+' ? 1 : -1) * ((d.a * den) / d.b), den });

/** Un item : { cas, op, g, d, den, res } ; null s'il sort du domaine. */
export const item = (cas, op, g, d, den) => {
    const res = resultat(g, op, d, den);
    if (!Number.isInteger(res.num) || res.num < 1 || res.num > NUM_MAX) return null;
    if (gcd(res.num, den) !== 1) return null;
    if (estSacrifie(g, d)) return null;
    return { cas, op, g, d, den, res };
};

/** Une fraction plus petite que 1, irréductible, de dénominateur b. */
export const fractionSous1 = (b) => {
    for (let k = 0; k < 40; k++) {
        const a = randInt(1, b - 1);
        if (gcd(a, b) === 1) return frac(a, b);
    }
    return frac(1, b);
};

/**
 * Un item d'un cas donné. `nMin` : entier minimal du cas entier ; `opForce` :
 * impose + ou − ; `grandAGauche` : cas multiples, le grand dénominateur à
 * gauche (tiré si null). null si 300 essais échouent (l'appelant retire).
 */
export const tirerItem = (cas, { nMin = 1, opForce = null, grandAGauche = null } = {}) => {
    for (let essai = 0; essai < 300; essai++) {
        const op = opForce || randomChoice(['+', '-']);
        let it = null;
        if (cas === 'meme_den') {
            const b = randInt(3, 10);
            const x = fractionSous1(b);
            const y = fractionSous1(b);
            if (x.a === y.a) continue;
            const [g, d] = op === '-' && x.a < y.a ? [y, x] : [x, y];
            it = item(cas, op, g, d, b);
        } else if (cas === 'entier') {
            const b = randInt(2, 10);
            it = item(cas, op, entier(randInt(nMin, 5)), fractionSous1(b), b);
        } else if (cas === 'multiples') {
            const b = randInt(2, 10);
            const k = randInt(2, 5);
            if (k * b > KB_MAX) continue;
            const petit = fractionSous1(b);
            const grand = fractionSous1(k * b);
            const aGauche = grandAGauche ?? randomChoice([true, false]);
            const [g, d] = aGauche ? [grand, petit] : [petit, grand];
            it = item(cas, op, g, d, k * b);
        } else {
            const [p, q] = shuffle(randomChoice(COUPLES_PREMIERS));
            it = item(cas, op, fractionSous1(p), fractionSous1(q), p * q);
        }
        if (it) return it;
    }
    return null;
};

/** Replis déterministes vérifiés, hors sacrifices, un par cas : dénominateurs communs 7, 3, 21, 10 ; deux soustractions ; 11/3 > 1. */
export const REPLIS = {
    meme_den: item('meme_den', '+', frac(2, 7), frac(3, 7), 7),
    entier: item('entier', '-', entier(4), frac(1, 3), 3),
    multiples: item('multiples', '-', frac(4, 7), frac(2, 21), 21),
    premiers: item('premiers', '+', frac(1, 2), frac(1, 5), 10),
};
// ─── Écritures ────────────────────────────────────────────────────────────

export const fracTex = (n, d) => `\\dfrac{${n}}{${d}}`;
const operandeTex = (f) => (estEntier(f) ? String(f.a) : fracTex(f.a, f.b));
export const exprTex = (it) => `${operandeTex(it.g)} ${it.op} ${operandeTex(it.d)}`;
export const resTex = (r) => fracTex(r.num, r.den);
export const memeValeur = (u, v) => u.num * v.den === v.num * u.den;

export const EXPLICATIONS = {
    bas_additionnes: 'On n\'ajoute pas les dénominateurs : le dénominateur dit la taille des parts, seuls les numérateurs s\'ajoutent ou se retranchent.',
    entier_pas_converti: 'Un entier, c\'est des parts entières : 3 = 15 cinquièmes. Je l\'écris sur le dénominateur avant d\'ajouter.',
    pas_converti: 'Le dénominateur commun est bon, mais chaque fraction convertie change aussi de numérateur : 4/7, c\'est 12/21.',
    coller: 'On ne colle pas les chiffres : l\'entier se convertit en parts, 3 = 15/5, puis on ajoute les parts.',
    voisin_meme_table: 'Je vérifie le fait de table : 7 × 3 = 21, donc 4/7 = 12/21, ni 8/21 ni 16/21.',
    regle: 'Des parts de la même taille d\'abord, puis j\'ajoute ou je retire les numérateurs.',
};

// ─── Distracteurs ─────────────────────────────────────────────────────────

const signe = (op) => (op === '+' ? 1 : -1);

/**
 * TOUS les candidats d'un item (chaque variante d'une famille), chacun avec sa
 * valeur exacte ; seulement ceux strictement positifs, jamais la clé en
 * valeur, jamais deux fois la même valeur.
 */
export const tousCandidats = (it) => {
    const { g, d, op, den, res } = it;
    const s = signe(op);
    const c = [];
    const pousser = (code, num, dd) => { if (num >= 1 && dd >= 1) c.push({ code, val: { num, den: dd } }); };
    if (it.cas === 'entier') {
        const n = g.a;
        pousser('entier_pas_converti', n + s * d.a, d.b);
        pousser('bas_additionnes', n + s * d.a, 1 + d.b);
        if (op === '+') pousser('coller', Number(`${n}${d.a}`), d.b);
        for (const dv of [-1, 1]) pousser('voisin_meme_table', n * d.b + dv + s * d.a, d.b);
    } else if (it.cas === 'meme_den') {
        pousser('bas_additionnes', g.a + s * d.a, g.b + d.b);
        for (const dv of [-1, 1]) pousser('voisin_meme_table', g.a + s * d.a + dv, den);
    } else if (it.cas === 'multiples') {
        const [grand, petit] = g.b > d.b ? [g, d] : [d, g];
        const k = den / petit.b;
        const sPetit = petit === g ? 1 : s;
        const sGrand = grand === g ? 1 : s;
        pousser('bas_additionnes', g.a + s * d.a, g.b + d.b);
        pousser('pas_converti', sPetit * petit.a + sGrand * grand.a, den);
        for (const kv of [k - 1, k + 1]) if (kv >= 1) pousser('voisin_meme_table', sPetit * kv * petit.a + sGrand * grand.a, den);
    } else {
        pousser('bas_additionnes', g.a + s * d.a, g.b + d.b);
        pousser('pas_converti', g.a + s * d.a, den);
        pousser('pas_converti', g.a * d.b + s * d.a, den);
        pousser('pas_converti', g.a + s * d.a * g.b, den);
        for (const dv of [-1, 1]) {
            pousser('voisin_meme_table', g.a * (d.b + dv) + s * d.a * g.b, den);
            pousser('voisin_meme_table', g.a * d.b + s * d.a * (g.b + dv), den);
        }
    }
    const vus = [res];
    return c.filter((x) => {
        if (vus.some((v) => memeValeur(v, x.val))) return false;
        vus.push(x.val);
        return true;
    }).map((x) => ({ ...x, texte: `$${resTex(x.val)}$` }));
};

/** Une variante au hasard par famille (Vrai/Faux). */
export const candidats = (it) => {
    const parCode = new Map();
    for (const x of shuffle(tousCandidats(it))) if (!parCode.has(x.code)) parCode.set(x.code, x);
    return [...parCode.values()];
};

/**
 * QCM : bas_additionnes (l'erreur principale, au dénominateur différent) puis
 * deux familles distinctes au dénominateur commun, choisies pour que le RANG
 * de la clé parmi les trois choix au dénominateur commun (plus petit, médian,
 * plus grand) soit tiré uniformément parmi les rangs que les conceptions
 * permettent : les erreurs de conversion donnent presque toutes MOINS que la
 * clé (revue 2026-09-09 : clé médiane 55 %, plus petite 3 %), on ne masque
 * pas ça par une famille inventée, on choisit parmi les vraies. null si
 * moins de deux familles disponibles.
 */
export const distracteursQcm = (it, cands = tousCandidats(it)) => {
    const fixe = cands.find((x) => x.code === 'bas_additionnes');
    if (!fixe) return null;
    const communs = cands.filter((x) => x.code !== 'bas_additionnes' && x.val.den === it.den);
    const valeur = (v) => v.num / v.den;
    const parRang = new Map();
    for (let i = 0; i < communs.length; i++) {
        for (let j = i + 1; j < communs.length; j++) {
            const a = communs[i]; const b = communs[j];
            if (a.code === b.code || memeValeur(a.val, b.val)) continue;
            const rang = [valeur(a.val), valeur(b.val)].filter((v) => v < valeur(it.res)).length;
            if (!parRang.has(rang)) parRang.set(rang, []);
            parRang.get(rang).push([a, b]);
        }
    }
    if (!parRang.size) return null;
    // Le rang « médian » est presque toujours disponible, « plus petite » rarement :
    // tirage pondéré (plus petite 3, plus grande 2, médiane 1) pour approcher
    // des tiers sur le parc. Mesuré à la revue sur 5 000 tirages : plus petite
    // 25 %, médiane 37 %, plus grande 38 % (avant sélection : 3 / 55 / 29) ;
    // la meilleure heuristique de position plafonne à 38 %, résidu assumé.
    const POIDS = { 0: 3, 2: 2, 1: 1 };
    const urne = [...parRang.keys()].flatMap((r) => Array(POIDS[r]).fill(r));
    const rang = randomChoice(urne);
    const [a, b] = randomChoice(parRang.get(rang));
    return [fixe, a, b];
};

// ─── Carte 1 : Série flash (4 lignes, saisie fraction deux cases) ─────────

const CONSIGNE_FLASH = '<p><b>Calcule.</b> Écris le résultat en une seule fraction.</p>';

/** « g op d = ⬚/⬚ », deux cases (gabarit 622), étais pour les champs de saisie. */
export const ligneFlash = (it) => `${exprTex(it)} = \\dfrac{{{${it.res.num}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${it.res.den}}}}`;

export const phraseFlash = (items) => [items.slice(0, 2), items.slice(2, 4)].map((paire) => `$${paire.map(ligneFlash).join(' \\quad ; \\quad ')}$`).join('\n');

const payloadFlash = (items) => {
    const payload = makeTexteTrou(phraseFlash(items), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, bas_additionnes: EXPLICATIONS.bas_additionnes } };
    payload._meta.params = { items };
    return payload;
};

const genFlash = () => {
    for (let essai = 0; essai < 200; essai++) {
        // deux soustractions sur quatre, l'entier ≥ 2 (le discriminant)
        const ops = shuffle(['+', '+', '-', '-']);
        const items = CAS.map((cas, i) => tirerItem(cas, { nMin: 2, opForce: ops[i] }));
        if (items.some((it) => !it)) continue;
        if (new Set(items.map((it) => it.den)).size < 4) continue;
        if (!items.some((it) => it.res.num > it.res.den)) continue;
        return payloadFlash(shuffle(items));
    }
    return payloadFlash(CAS.map((cas) => REPLIS[cas]));
};

// ─── Carte 2, format A : QCM, quatre choix ────────────────────────────────
// Supprimé à la revue (clé médiane 55 %), puis REMIS le même jour : le QCM
// est le seul format que le mode classe par QR code sait dépouiller (règle
// générale, un QCM par attendu). La fuite de position est traitée par la
// sélection des distracteurs (distracteursQcm), pas par une famille inventée.

const genQcm = () => {
    for (let essai = 0; essai < 200; essai++) {
        // meme_den exclu (une seule erreur possible) ; entier en addition seulement ;
        // multiples et premiers dans les deux opérations et les deux positions
        const cas = randomChoice(['entier', 'multiples', 'premiers']);
        const it = tirerItem(cas, cas === 'entier' ? { opForce: '+' } : {});
        if (!it) continue;
        const faux = distracteursQcm(it);
        if (!faux) continue;
        const bon = `$${resTex(it.res)}$`;
        const choix = [bon, ...faux.map((f) => f.texte)];
        if (new Set(choix).size !== 4) continue;
        const texte = `<p><b>Quel est le résultat de ${tex(exprTex(it))} ?</b></p>`;
        const payload = makeQcm(choix, bon, texte, opts(L_MEME));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...it, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli déterministe vérifié : 1/2 + 1/5 = 7/10 ; bas_additionnes 2/7, pas_converti 2/10, voisin 8/10.
    const it = REPLIS.premiers;
    const bon = `$${resTex(it.res)}$`;
    const payload = makeQcm([bon, `$${fracTex(2, 7)}$`, `$${fracTex(2, 10)}$`, `$${fracTex(8, 10)}$`], bon,
        `<p><b>Quel est le résultat de ${tex(exprTex(it))} ?</b></p>`, opts(L_MEME));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...it, distracteurs: [{ code: 'bas_additionnes' }, { code: 'pas_converti' }, { code: 'voisin_meme_table' }] };
    return payload;
};

// ─── Carte 2, format B : Vrai ou faux (3 égalités) ────────────────────────

// A2 mesuré à la revue (5 000 tableaux) : avec bas_additionnes imposée sur
// la première fausse, « dénominateur différent → faux, sinon vrai » était
// juste sur 79 % des lignes. Règle supprimée (épreuve de Musk) : chaque
// fausse tire sa famille au hasard parmi les candidats de son item ;
// bas_additionnes reste dans quatre tableaux sur dix. Le résidu (~67 %) est
// STRUCTUREL et assumé : les vraies ont forcément le dénominateur commun,
// et reconnaître une fausse à son dénominateur est la conception visée.

/** « g op d = fraction », vraie ou fausse (famille tirée dans les candidats). */
export const affirmation = (it, vraie, cands = candidats(it)) => {
    if (vraie) return { ligne: tex(`${exprTex(it)} = ${resTex(it.res)}`), code: null };
    if (!cands.length) return null;
    const d = randomChoice(cands);
    return { ligne: tex(`${exprTex(it)} = ${resTex(d.val)}`), code: d.code };
};

const genVf = () => {
    for (let essai = 0; essai < 200; essai++) {
        const cas = shuffle([...CAS]).slice(0, 3);
        const items = cas.map((c) => tirerItem(c));
        if (items.some((it) => !it)) continue;
        const nbVraies = randomChoice([1, 2]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(3 - nbVraies).fill(false)]);
        const affs = items.map((it, i) => affirmation(it, verites[i]));
        if (affs.some((a) => !a)) continue;
        const lignes = affs.map((a) => a.ligne);
        if (new Set(lignes).size < 3) continue;
        const payload = makeVraiFaux(lignes, verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_MEME));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items, verites, distracteurs: affs.filter((a) => a.code).map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli déterministe : 2/7 + 3/7 = 5/7 vrai ; 4/7 − 2/21 = 2/28 faux (bas_additionnes) ; 4 − 1/3 = 10/3 faux (voisin_meme_table).
    const items = [REPLIS.meme_den, REPLIS.multiples, REPLIS.entier];
    const lignes = [
        tex(`${exprTex(REPLIS.meme_den)} = ${fracTex(5, 7)}`),
        tex(`${exprTex(REPLIS.multiples)} = ${fracTex(2, 28)}`),
        tex(`${exprTex(REPLIS.entier)} = ${fracTex(10, 3)}`),
    ];
    const payload = makeVraiFaux(lignes, [0, 1, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_MEME));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items, verites: [true, false, false], distracteurs: [{ code: 'bas_additionnes' }, { code: 'voisin_meme_table' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre cas (même dénominateur, entier, multiples, premiers entre eux) ; fractions d\'énoncé < 1 irréductibles ; résultat > 0, numérateur ≤ 50, premier avec le dénominateur commun ; quatre exemples sacrifiés (3/5 + 4/5, 3 + 2/5, 2/3 + 1/6, 2/5 + 1/4)',
        'chaque distracteur est vérifié strictement positif, différent de la clé et des autres EN VALEUR',
        'chaque distracteur porte son code dans _meta.params.distracteurs',
    ],
    meme_qcm_unique: {
        support: '« Quel est le résultat de g op d ? », cas entier (addition seulement), multiples ou premiers (deux opérations, deux positions) ; meme_den exclu (une seule erreur possible). Supprimé à la revue (clé médiane 55 %) puis remis : seul format dépouillable en mode classe QR code (règle générale 2026-09-09)',
        fixes: { bas_additionnes: '(x ± y)/(b + d), l\'erreur principale, au dénominateur différent' },
        rotation: 'deux familles distinctes au dénominateur commun parmi entier_pas_converti, coller, pas_converti, voisin_meme_table, choisies pour que le rang de la clé parmi les trois choix au dénominateur commun soit tiré uniformément parmi les rangs que les conceptions permettent (aucune famille inventée) ; résidu mesuré à la revue',
    },
    meme_tableau_vf: {
        support: 'trois égalités g op d = fraction, trois cas distincts, 1 ou 2 vraies ; chaque fausse tire sa famille au hasard (aucune imposée : « bas_additionnes sur la première fausse » donnait 79 % à l\'heuristique du dénominateur, revue 2026-09-09 ; résidu ~67 % structurel assumé)',
        familles: {
            bas_additionnes: '(x ± y)/(b + d), l\'erreur principale ; sur le cas entier (n ± a)/(1 + b)',
            entier_pas_converti: '(n ± a)/b',
            pas_converti: 'bon dénominateur commun, numérateurs tels quels (un ou deux)',
            coller: 'chiffres collés, 3 + 2/5 = 32/5 (entier, addition)',
            voisin_meme_table: 'conversion avec un fait de table voisin',
        },
    },
    meme_relier: 'SUPPRIMÉ au cadrage (épreuve de Musk) : redite de la Série flash, comme à la revue de 1053',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    meme_qcm_unique: genQcm,
    meme_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'meme', label: L_MEME, formatsList: [
        { key: 'meme_qcm_unique', label: 'QCM' },
        { key: 'meme_tableau_vf', label: 'Vrai/Faux' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
